import moment from "moment";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import { fixedProgramAcces } from "src/DAL/Programmes/Programmes";
import { gr } from "src/design-system/tokens";
import GrDialog from "src/design-system/components/GrDialog";
import GrButton from "src/design-system/components/GrButton";
import GrTextField from "src/design-system/components/GrTextField";
import GrChoiceChips from "src/design-system/components/GrChoiceChips";
import GrCheckbox from "src/design-system/components/GrCheckbox";
import GrText from "src/design-system/components/GrText";

/**
 * Start a limited programme: the member picks a start date (today or later) and, when the
 * programme has difficulty levels, a level + confirms the level note.
 * Same API and payload as before: start_limited_program/by_member { program_id, difficulty_level, start_date }.
 * `programId` is the member's programme entry from the list (its `_id` is the programme).
 */

const LEVELS = [
  { value: "novice", label: "Novice" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
];

const fieldsSx = { display: "grid", gap: gr.space[5], mt: gr.space[5] };
const labelSx = {
  display: "block",
  mb: gr.space[2],
  fontSize: gr.font.size.sm,
  fontWeight: gr.font.weight.semibold,
  color: gr.color.ink,
};
const errorSx = { mt: gr.space[1], color: gr.color.danger };
const nameSx = { fontWeight: gr.font.weight.semibold, color: gr.color.ink };

function ConfirmationWithDate({ open, setOpen, title, programId, reload }) {
  const { enqueueSnackbar } = useSnackbar();
  const programme = programId?._id || {};
  const hasLevels = Boolean(programme.difficulty_level);
  const weeks = programme.no_of_weeks;
  const today = moment().format("YYYY-MM-DD");

  const [date, setDate] = useState("");
  const [level, setLevel] = useState(programId?.member_difficutly_level || "");
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDate("");
      setLevel(programId?.member_difficutly_level || "");
      setAgreed(false);
      setErrors({});
      setSaving(false);
    }
  }, [open, programId]);

  const close = () => setOpen(false);

  const start = async () => {
    const next = {};
    if (!date) next.date = "Please pick a start date.";
    else if (moment(date).isBefore(today, "day")) next.date = "The start date can't be in the past.";
    if (hasLevels && !level) next.level = "Please choose your level.";
    if (hasLevels && !agreed) next.agree = "Please tick this box to continue.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    const result = await fixedProgramAcces({
      program_id: programme._id,
      difficulty_level: level,
      start_date: moment(date).format("YYYY-MM-DD"),
    });
    if (result?.code === 200) {
      localStorage.removeItem("difficultyLevel");
      localStorage.removeItem("isOpen");
      setSaving(false);
      close();
      reload();
    } else {
      setSaving(false);
      enqueueSnackbar(result?.message || "Could not start this programme", { variant: "error" });
    }
  };

  const ends = date && weeks ? moment(date).add(weeks, "weeks").format("D MMM YYYY") : "";
  const name = programId?.title || programme.title;

  return (
    <GrDialog
      open={open}
      onClose={saving ? undefined : close}
      title={title || "Start programme"}
      actions={
        <>
          <GrButton variant="quiet" onClick={close} disabled={saving}>
            Cancel
          </GrButton>
          <GrButton onClick={start} loading={saving}>
            Start programme
          </GrButton>
        </>
      }
    >
      <GrText variant="body" sx={{ color: gr.color.ink2 }}>
        {name ? (
          <>
            Choose the day you want to start{" "}
            <Box component="strong" sx={nameSx}>
              {name}
            </Box>
            {weeks ? `. It runs for ${weeks} week${weeks === 1 ? "" : "s"} from then.` : "."}
          </>
        ) : (
          "Choose the day you want to start this programme."
        )}
      </GrText>

      <Box sx={fieldsSx}>
        <Box>
          <GrTextField
            id="programme-start-date"
            type="date"
            label="Start date"
            fullWidth
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setErrors((current) => ({ ...current, date: undefined }));
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: today }}
            error={Boolean(errors.date)}
            helperText={errors.date || (ends ? `Ends ${ends}` : " ")}
          />
        </Box>

        {hasLevels ? (
          <>
            <Box>
              <Box component="span" sx={labelSx}>
                Your level
              </Box>
              <GrChoiceChips
                ariaLabel="Your level"
                options={LEVELS}
                value={level}
                onChange={(value) => {
                  setLevel(value);
                  setErrors((current) => ({ ...current, level: undefined }));
                }}
                error={Boolean(errors.level)}
              />
              {errors.level ? (
                <GrText variant="caption" sx={errorSx}>
                  {errors.level}
                </GrText>
              ) : null}
            </Box>
            <Box>
              <GrCheckbox
                id="programme-level-agree"
                checked={agreed}
                onChange={(value) => {
                  setAgreed(value);
                  setErrors((current) => ({ ...current, agree: undefined }));
                }}
                error={Boolean(errors.agree)}
                label="Workouts with your selected difficulty level will be assigned to you for this programme. (To change it, please submit a support ticket.)"
              />
              {errors.agree ? (
                <GrText variant="caption" sx={errorSx}>
                  {errors.agree}
                </GrText>
              ) : null}
            </Box>
          </>
        ) : null}
      </Box>
    </GrDialog>
  );
}

export default ConfirmationWithDate;
